import React, { useState, useEffect, useMemo } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpIcon,
  PlusIcon,
  ArrowDownOnSquareStackIcon,
} from "@heroicons/react/24/outline";
import { obtenerJuegos } from "../../api";

const Games = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [gamesData, setGamesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const loadGames = async () => {
      try {
        const games = await obtenerJuegos();
        const mapped = games.map((g) => ({
          id: g.id,
          title: g.name || g.nombre,
          duration: `${g.duration || g.duracion}m`,
          price: `$${g.base_price || g.precio_base}`,
          imageUrl:
            g.image_url || g.imagen_url ||
            "https://via.placeholder.com/300x200?text=Laser+Tag",
          category: g.category_name || g.categoria_nombre || "Uncategorized",
        }));
        setGamesData(mapped);
      } catch (error) {
        console.error("Error loading games:", error);
      } finally {
        setLoading(false);
      }
    };

    loadGames();
  }, []);

  const uniqueCategories = useMemo(() => {
    const categorias = gamesData.map((g) => g.category).filter(Boolean);
    return [...new Set(categorias)];
  }, [gamesData]);

  const filteredGames = useMemo(() => {
    return gamesData.filter(
      (g) =>
        g.title.toLowerCase().includes(search.toLowerCase()) &&
        (selectedCategory === "" || g.category === selectedCategory)
    );
  }, [search, selectedCategory, gamesData]);

  if (loading) return <div className="p-8 text-white">Loading games...</div>;

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="w-64 p-4 bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))] border rounded-lg">
        <div className="relative mb-6">
          <FaSearch className="absolute left-3 top-3 text-[rgb(var(--color-text))]/60" />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-3 py-2 w-full bg-hover-bg text-primary rounded"
          />
        </div>

        <div className="space-y-2">
          <h3 className="uppercase text-xs text-[rgb(var(--color-text))]/70">Categories</h3>
          <ul className="space-y-1">
            <li>
              <button
                className={`w-full text-left px-2 py-1 rounded ${
                  selectedCategory === "" ? "bg-hover-bg" : "hover:bg-hover-bg"
                }`}
                onClick={() => setSelectedCategory("")}
              >
                All
              </button>
            </li>
            {uniqueCategories.map((cat) => (
              <li key={cat}>
                <button
                  className={`w-full text-left px-2 py-1 rounded ${
                    selectedCategory === cat
                      ? "bg-hover-bg"
                      : "hover:bg-hover-bg"
                  }`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto p-6 bg-[rgb(var(--color-bg))]">
        <div className="flex justify-between items-center mb-6 text-[rgb(var(--color-text))]">
          <button className="flex items-center space-x-1 hover:text-primary">
            <ArrowUpIcon className="h-5 w-5" />
            <span>SORT BY REFERENCE ASCENDING</span>
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin/games/create')}
              className="flex items-center space-x-1 px-3 py-1 bg-primary text-white rounded hover:bg-hover-bg"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create</span>
            </button>
            <button className="flex items-center space-x-1 px-3 py-1 border  text-[rgb(var(--color-text))] rounded hover:border-[rgb(var(--color-text))]">
              <ArrowDownOnSquareStackIcon className="h-5 w-5" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {filteredGames.map((game) => (
            <div
              key={game.id}
              onClick={() => navigate(`/admin/games/${game.id}`)}
              className="cursor-pointer group bg-gradient-to-br from-primary/70 to-secondary/70 border rounded-2xl shadow-xl overflow-hidden transform transition-transform duration-300 hover:scale-105"
            >
              {/* Imagen 16:9 */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={game.imageUrl}
                  alt={game.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/30"></div>

                {/* Badge categoría */}
                <div className="absolute top-2 left-2 bg-[rgb(var(--color-text))] text-[rgb(var(--color-bg))] text-xs font-semibold px-2 py-1 rounded-full shadow">
                  {game.category}
                </div>
              </div>

              {/* Info */}
              <div className="p-4 text-[rgb(var(--color-text))]">
                <h3 className="text-lg font-audiowide truncate">
                  {game.title}
                </h3>
                <div className="text-sm text-[rgb(var(--color-text))]/80 flex justify-between mt-1">
                  <span>{game.duration}</span>
                  <span>{game.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Games;
